import React, { createRef, useState, useEffect, useCallback } from "react";
import { View, StyleSheet, Linking, Image, ScrollView, SafeAreaView, Platform } from "react-native";
import { Grid, Container, Row, Column } from "ui-grid";
import {
  Button,
  Checkbox,
  fontWeights,
  Icon,
  Text,
  theme,
  typography,
  ErrorWarningBottomSheet,
} from "ui-components";
import { Spacer } from "../../components";
import { useNavigation, useFocusEffect, CommonActions } from "@react-navigation/native";
import { translate } from "../../i18n";
import { CheckboxStatusType } from "../../../types";
import { useDispatch, useSelector } from "react-redux";
import { toHumanDollars } from "../../utils/money";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { reviewCertifiedBAPaymentSchema } from "./utils/validation-schemas-payment";
import { useConfig } from "../../hooks/useConfig";
import { InfoBoxType } from "../../enums";
import { setPaymentInReview } from "../../redux/actions/actionCreators";
import { formatDate, formatDateDo, getDisplayableLocalTime } from "../../utils/date";
import { HomeParamsList, HomeScreenProps } from "../../navigation/home-stack/home-stack";
import { getUnitNumber, objectToQueryStringParams } from "../../utils";
import { RootState } from "../../redux/reducers";
import { UserService } from "../../api/UserService";
import {
  DynamoStreamMachineService,
  STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_COMMANDS,
  STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES,
} from "../../api/DynamoStreamMachineService";
import { StateToActionMapping } from "../../enums/certified-ba";
import { domusoResponsiveStyleHelper } from "./utils/domuso-responsive";
import { Footer } from "../../components/new/footer/footer";
import { BottomNav } from "../../components/layout/bottom-nav";
import automationTestingLocators from "./utils/automation-testing-locators.json";
import { PaymentHeading } from "../../components/new/payment-flow/payment-flow-heading";
import * as storage from "../../utils/storage";
import { getErrorMessageId, ERROR_CODES } from "../../utils/errorCodes";
import { useDetermineChannel } from "../../hooks/useDetermineChannel";

export const PaymentFlowPaymentMethodCertifiedACHYodleeWidgetReviewPayment = ({
  route,
}: HomeScreenProps) => {
  const navigation = useNavigation();
  let { params = {} as HomeParamsList } = route;
  const logger = useSelector((state: RootState) => state.logger);
  let {
    propName,
    propertyUnit,
    paymentDate,
    reservationId,
    amounts = {},
    paymentSourceId,
    paymentScheduleType,
    authorizationDate,
    stateMachineFromPrevScreen,
    stateMachineEntityID,
    isCertifiedBA,
    isDelayedDcash = false,
  } = params;

  // These are all bigs
  const { amount, platformFee, total } = amounts;
  const config = useConfig();
  const dispatch = useDispatch();
  const formattedPaymentDate = formatDate(paymentDate);
  const formattedAuthorizationDate = formatDate(authorizationDate);
  const formattedAmount = toHumanDollars(amount);
  const formattedPlatformFee = toHumanDollars(platformFee);
  const formattedTotal = toHumanDollars(total);
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const userService = new UserService(cognitoUser, dispatch);
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const dynamoStreamMachineService = new DynamoStreamMachineService(cognitoUser, dispatch);
  // To keep track of the created state machine when calling DyanmoStreamMachineService
  const [stateMachine, setStateMachine] = useState<any>(null);
  // Keeping track of dynamo stream machine entity ID.
  const [currentStateMachineEntityID, setCurrentStateMachineEntityID] = useState<string>(null);
  // Used to control the polling flow.
  const [needToPoll, setNeedToPoll] = useState(false);
  // Used for tracking the current states that we are trying to poll from stream machine.
  const [stateMachineStatesToPoll, setStateMachineStatesToPoll] = useState<string[]>([]);
  const [cancellationRefundCheck, setCancellationRefundCheck] = useState<boolean>(false);
  const [returnWarningCheck, setReturnWarningCheck] = useState<boolean>(false);
  const refPopupWarning = createRef();
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [channel] = useDetermineChannel();

  const [cof, setCof] = useState<CheckboxStatusType>("checked");
  const [cancellationRefund, setCancellationRefund] = useState<CheckboxStatusType>("unchecked");
  const [returnWarning, setReturnWarning] = useState<CheckboxStatusType>("unchecked");
  let defaultValues: any = {
    cof,
    cancellationRefund,
    returnWarning,
  };
  const queryParams: any = {
    scheduleType: paymentScheduleType === "RECURRING" ? "RECURRING" : "ONE_SHOT",
    amount,
    total,
    platformFee,
    reservationId,
    paymentSourceId,
    userNowIso: getDisplayableLocalTime(new Date()).format(),
    paymentDate,
  };
  const pdfUrl = `${
    config.docGenerationService
  }/generateCancellationPolicyPDF?${objectToQueryStringParams(queryParams)}`;

  const isContinueDisabled =
    isCreatingPayment || (returnWarningCheck && cancellationRefundCheck) ? false : true;

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(reviewCertifiedBAPaymentSchema),
    defaultValues,
  });

  const layoutEffect = useCallback(() => {
    dispatch(setPaymentInReview(reservationId));
    navigation.dispatch((state) => {
      //Previous screen (PaymentFlowPaymentMethodCertifiedACHYodleeWidget) needs to be reemoved
      // from routes, so when back button is pressed it navigates to PaymentFlowPaymentMethodCertifiedACH
      const routes = state.routes.filter(
        (r) => r.name !== "PaymentFlowPaymentMethodCertifiedACHYodleeWidget"
      );

      return CommonActions.reset({
        ...state,
        index: routes.length - 1,
        routes: routes,
      });
    });
  }, []);
  useFocusEffect(layoutEffect);

  useEffect(() => {
    const clearYodleeState = async () => {
      // Clear persisted yodlee widget data no longer need it.
      if (Platform.OS === "web") {
        await storage.remove("YODLEEWIDGET_PERSISTANCE_KEY");
      }
    };
    clearYodleeState();
  }, []);

  useEffect(() => {
    register("cof");
    setStateMachine(stateMachineFromPrevScreen);
    setCurrentStateMachineEntityID(stateMachineEntityID);
    register("returnWarning");
  }, [register]);

  // This effect is used for polling the DynamoStreamMachine if .
  useEffect(() => {
    (async () => {
      logger.log(
        `useEffect state machine state polling called --> currentStateMachineEntityID: ${currentStateMachineEntityID}`
      );
      if (!stateMachine) {
        logger.log("No state machine created skipping cannot do the state machine polling");
        return;
      }
      // If need to poll and there states to look for
      if (needToPoll && stateMachineStatesToPoll.length > 0) {
        logger.log(
          `Should be polling: ${needToPoll} streamMachineStatesToPoll: ${stateMachineStatesToPoll}`
        );
        dynamoStreamMachineService.pollMachineState(
          stateMachine.machineId,
          currentStateMachineEntityID,
          stateMachineStatesToPoll,
          60,
          setStateMachine
        );
      }
    })();
  }, [needToPoll, stateMachineStatesToPoll]);

  // Effect is used to take an action based on updated StateMachine which is updated from Polling function.
  useEffect(() => {
    logger.log(
      `useEffect action trigger called --> currentStateMachineEntityID: ${currentStateMachineEntityID}`
    );
    if (!stateMachine) {
      logger.log("No stateMachine detected");
      return;
    }
    const stateMachineStateToActionMapping: StateToActionMapping = {
      [STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PAYMENT_CREATED]: onPaymentCreatedState,
      [STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PAYMENT_FAILED]: onPaymentFailedState,
    };
    if (Object.keys(stateMachineStateToActionMapping).includes(stateMachine.item.machine_state)) {
      const action = stateMachineStateToActionMapping[stateMachine.item.machine_state];
      logger.log(
        `State machine ${stateMachine.item.machine_state} State polled, 
          Action: ${action.name}`
      );
      action();
    } else {
      logger.log(
        `No action defined for the state and will do the default failure action--> ${stateMachine.item.machine_state}`
      );
    }
    logger.log("No need to poll anymore");
    setNeedToPoll(false);
  }, [stateMachine]);

  const onPaymentCreatedState = async () => {
    userService.sendFootprints({
      payment_schedule_id: stateMachine.item.mysql_payment_schedule_id,
      username: userInfo.username,
      interaction_type: "PAYMENT_SCHEDULE",
      channel,
    });

    const messageBody =
      paymentScheduleType === "RECURRING"
        ? translate("paymentFlow.paymentMethodCertifiedBank.reviewPayment.autoPayScheduleSuccess", {
            formattedDate: formatDateDo(paymentDate),
          })
        : isCertifiedBA
        ? translate("paymentFlow.paymentMethodCertifiedBank.reviewPayment.certifiedManagerApproval")
        : translate(
            "paymentFlow.paymentMethodCertifiedBank.reviewPayment.paymentPendingManagerApproval"
          );
    setIsCreatingPayment(false);
    logger.infoObject({
      reservationId: reservationId,
      paymentSourceId: paymentSourceId,
      paymentScheduleType: paymentScheduleType,
      message: "Create payment succeeded",
      paymentSourceType: "BANK_ACCOUNT_DOMUSO_CASH",
    });
    navigation.navigate("PaymentFlowPaymentTrasmitted", {
      ...params,
      bankInfoText: translate("paymentMethods.certifiedBankAccount.title"),
      createPaymentResult: {
        type: "SUCCESS",
        infoBoxType: InfoBoxType.GREEN,
        headline: translate("paymentFlow.paymentMethodCertifiedBank.reviewPayment.success"),
        messageBody,
      },
    });
  };

  const onPaymentFailedState = async (exception) => {
    setIsCreatingPayment(false);
    let headline = translate(
      "paymentFlow.paymentMethodCertifiedBank.errors.genericACHPaymentErrorTitle"
    );
    let messageBody = translate(
      "paymentFlow.paymentMethodCertifiedBank.errors.genericACHPaymentErrorMessage"
    );
    if (stateMachine.item.result_code) {
      const errMsg = getErrorMessageId({ code: stateMachine.item.result_code });
      if (errMsg) {
        headline = translate(`${errMsg}_headline`);
        messageBody = translate(errMsg);
      }
    } else if (stateMachine.item.reason_code) {
      headline = translate(`errors.${stateMachine.item.reason_code}.title`);
      messageBody = translate(`errors.${stateMachine.item.reason_code}.message`);
    }

    const error =
      stateMachine?.item?.result_code || stateMachine?.item?.reason_code
        ? `resultCode: ${stateMachine?.item?.result_code} reasonCode: ${stateMachine?.item?.reason_code}`
        : exception
        ? exception
        : "There was no error provided";
    // pull error
    logger.error({
      exception: error,
      reservationId: reservationId,
      paymentSourceId: paymentSourceId,
      paymentScheduleType: paymentScheduleType,
      message: `Create payment error`,
      paymentSourceType: "BANK_ACCOUNT_DOMUSO_CASH",
    });

    navigation.navigate("PaymentFlowPaymentTrasmitted", {
      ...params,
      isUnapprovedDCash: !!stateMachine.item.reason_code,
      isFailedACH: true,
      createPaymentResult: {
        type: "FAILURE",
        infoBoxType: InfoBoxType.RED,
        headline,
        messageBody,
      },
    });
  };

  const onSubmit = async (data: any) => {
    // Loading spinner.
    setIsCreatingPayment(true);

    let attrs = {
      payment_date: paymentDate,
    };

    let updatedStateMachine;
    try {
      updatedStateMachine = await dynamoStreamMachineService.transitionMachine(
        stateMachine.machineId,
        currentStateMachineEntityID,
        stateMachine.item.machine_state,
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_COMMANDS.USER_CONFIRMS,
        attrs
      );
      setStateMachine(updatedStateMachine);
      setStateMachineStatesToPoll([
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PAYMENT_CREATED,
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PAYMENT_FAILED,
      ]);
      setNeedToPoll(true);
    } catch (e) {
      logger.error(e);
      const errorData = e?.error?.response?.data;
      onPaymentFailedState(errorData ? errorData : e);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <Spacer size="large" />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollableArea}>
        <PaymentHeading heading={translate("paymentFlow.progressTitle.paymentSummary")} />
        <Grid>
          <Container>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <Spacer size="medium" />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.rowTitle}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                      .propertyNameText}>
                    {`${propName}`}
                  </Text>
                  <Text
                    style={styles.rowTitleRight}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                      .propertyUnitText}>
                    {`Unit ${getUnitNumber(propertyUnit)}`}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <Spacer size="medium" />
                <RenderSeparator />
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            {authorizationDate && (
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <Spacer size="medium" />
                  <View style={styles.inputRowInner}>
                    <Text
                      style={styles.paymentDetailsLeftText}
                      {...automationTestingLocators
                        .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                        .authorizationDateTitle}>
                      {"Authorization Date"}
                    </Text>
                    <Text
                      style={[styles.paymentDetailsRightText]}
                      {...automationTestingLocators
                        .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                        .authorizationDateText}>
                      {formattedAuthorizationDate}
                    </Text>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            )}
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <Spacer size="medium" />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.paymentDetailsLeftText}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                      .paymentDateTitle}>
                    {translate("paymentFlow.summary.paymentDate")}
                  </Text>
                  <Text
                    style={[styles.paymentDetailsRightText]}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                      .paymentDateText}>
                    {formattedPaymentDate}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <Spacer size="medium" />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.paymentDetailsLeftText}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                      .paymentMethodTitle}>
                    {translate("paymentFlow.summary.paymentMethod")}
                  </Text>
                  <Text
                    style={[styles.paymentDetailsRightText]}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                      .paymentMethodText}>
                    {translate("paymentMethods.certifiedBankAccount.title")}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <Spacer size="medium" />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.paymentDetailsLeftText}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment.amountTitle}>
                    {translate("paymentFlow.summary.amount")}
                  </Text>
                  <Text
                    style={[styles.paymentDetailsRightText]}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment.amountText}>
                    {formattedAmount}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <Spacer size="medium" />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.paymentDetailsLeftText}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                      .platformFeeTitle}>
                    {translate("paymentFlow.summary.platformFee")}
                  </Text>
                  <Text
                    style={[styles.paymentDetailsRightText]}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                      .platformFeeText}>
                    {formattedPlatformFee}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <Spacer size="medium" />
                <RenderSeparator />
              </Column>
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <Spacer size="medium" />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.rowTitle}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment.totalTitle}>
                    {translate("paymentFlow.summary.total")}
                  </Text>
                  <Text
                    style={styles.rowTitleRight}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment.totalText}>
                    {formattedTotal}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>

            {errors.cancellationRefund ? (
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <Spacer size="medium" />
                  <View style={{ flex: 1 }} />
                  <>
                    <View
                      style={{
                        flexDirection: "row",
                        flex: 1,
                        alignContent: "center",
                        alignItems: "center",
                      }}>
                      <Icon color={theme.colors.alert} size={theme.iconSize.small} name="error" />
                      <Text
                        style={styles.alertText}
                        {...automationTestingLocators
                          .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                          .policyErrorText}>
                        {translate("paymentFlow.summary.errorCancellationAndRefund")}
                      </Text>
                    </View>
                    <Spacer size="medium" />
                  </>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            ) : null}

            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                {!errors.cancellationRefund && <Spacer size="medium" />}
                <View style={styles.inputRowInner}>
                  <View style={{ paddingTop: 3 }}>
                    <Checkbox
                      disabled={isCreatingPayment}
                      style={styles.checkboxStyle}
                      onValueChange={(newValue: boolean) => {
                        setValue("cancellationRefund", newValue ? "checked" : "unchecked");
                        setCancellationRefundCheck(newValue);
                        if (newValue) {
                          trigger();
                        }
                      }}
                      value={cancellationRefundCheck}
                      {...automationTestingLocators
                        .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                        .cancellationRefundPolicyCheckbox}
                    />
                  </View>
                  <Text
                    style={styles.policyText}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                      .cancellationRefundPolicyText}>
                    {`${translate("paymentFlow.summary.futureCertifiedPaymentAccept1")} `}
                    <Text onPress={() => Linking.openURL(pdfUrl)} style={{ color: "#3B53D2" }}>
                      {`${translate("paymentFlow.summary.futureCertifiedPaymentAccept2")}`}
                    </Text>
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>

            {errors.returnWarning ? (
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <Spacer size="medium" />
                  <View style={{ flex: 1 }} />
                  <>
                    <View
                      style={{
                        flexDirection: "row",
                        flex: 1,
                        alignContent: "center",
                        alignItems: "center",
                      }}>
                      <Icon color={theme.colors.alert} size={theme.iconSize.small} name="error" />
                      <Text
                        style={styles.alertText}
                        {...automationTestingLocators
                          .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                          .missingAcceptanceErrorText}>
                        {translate("paymentFlow.summary.errorMissingAcceptanceCheckbox")}
                      </Text>
                    </View>
                    <Spacer size="medium" />
                  </>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            ) : null}

            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                {!errors.returnWarning && <Spacer size="medium" />}
                <View style={styles.inputRowInner}>
                  <View style={{ paddingTop: 3 }}>
                    <Checkbox
                      disabled={isCreatingPayment}
                      style={styles.checkboxStyle}
                      onValueChange={(newValue: boolean) => {
                        setValue("returnWarning", newValue ? "checked" : "unchecked");
                        setReturnWarningCheck(newValue);
                        if (newValue) {
                          trigger();
                        }
                      }}
                      value={returnWarningCheck}
                      {...automationTestingLocators
                        .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                        .returnWarningCheckbox}
                    />
                  </View>
                  <Text
                    style={styles.policyText}
                    {...automationTestingLocators
                      .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment
                      .returnWarningText}>
                    {`${translate("paymentFlow.summary.returnWarningMessage")}`}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
          </Container>
          <ErrorWarningBottomSheet
            ref={refPopupWarning}
            height={190}
            title={translate("paymentFlow.summary.cancelPaymentWarningTitle") as string}
            body={translate("paymentFlow.summary.cancelPaymentWarningSubTitle") as string}
            alertType={"warning"}
            leftButtonType={"secondary"}
            leftButtonText={
              translate("paymentFlow.summary.cancelPaymentWarningCancelButtonText") as string
            }
            rightButtonText={
              translate("paymentFlow.summary.cancelPaymentWarningStayButtonText") as string
            }
            onPressLeftButton={() => {
              navigation.navigate("Home");
              refPopupWarning?.current?.close();
            }}
            onPressRightButton={() => refPopupWarning?.current?.close()}
          />
        </Grid>
      </ScrollView>
      <BottomNav backgroundColor={theme.colors.white}>
        <View style={{ marginTop: 15 }} />
        <Row>
          <Column span={{ xs: false, sm: 3, md: 3, lg: 3 }} />
          <Column span={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
            <Button
              text={translate("paymentFlow.summary.cancel") as string}
              onPress={() => {
                refPopupWarning?.current?.open();
              }}
              type={"secondary"}
              {...automationTestingLocators
                .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment.cancelButton}
            />
            <View style={{ paddingVertical: 5 }}></View>
            <View style={{ paddingVertical: 5 }}>
              <Button
                type={"primary"}
                text={translate("paymentFlow.summary.submitPayment") as string}
                loading={isCreatingPayment}
                disabled={isCreatingPayment}
                style={{
                  backgroundColor:
                    isCreatingPayment || isContinueDisabled
                      ? theme.colors.text2
                      : theme.colors.primary,
                }}
                onPress={handleSubmit(onSubmit)}
                {...automationTestingLocators
                  .paymentFlowPaymentMethodCertifiedAchYodleeWidgetReviewPayment.confirmButton}
              />
            </View>
          </Column>
        </Row>
      </BottomNav>
      <Footer />
    </SafeAreaView>
  );
};

const RenderSeparator = () => {
  return <View style={styles.separator} />;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...domusoResponsiveStyleHelper({
      webPhone: {
        backgroundColor: "#FFFFFF",
      },
      webTablet: {
        backgroundColor: "#FAFAFA",
      },
      webDesktop: {
        backgroundColor: "#FAFAFA",
      },
      iosPhone: {
        backgroundColor: "#FFFFFF",
      },
      iosTablet: {
        backgroundColor: "#FAFAFA",
      },
      androidPhone: {
        backgroundColor: "#FFFFFF",
      },
      androidTablet: {
        backgroundColor: "#FAFAFA",
      },
    }),
  },
  scrollableArea: {
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: "#FFFFFF", paddingBottom: 10 },
      webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      iosPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
      iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      androidPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
      androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
    }),
  },
  scrollView: {
    flexGrow: 0,
  },
  inputRow: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  inputRowInner: {
    flexDirection: "row",
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      webDesktop: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
    }),
  },
  separator: {
    borderColor: theme.colors.stroke,
    borderTopWidth: 0.7,
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        marginLeft: 26,
        marginRight: 26,
      },
      webDesktop: {
        marginLeft: 26,
        marginRight: 26,
      },
      iosPhone: {},
      iosTablet: {
        marginLeft: 26,
        marginRight: 26,
      },
      androidPhone: {},
      androidTablet: {
        marginLeft: 26,
        marginRight: 26,
      },
    }),
  },
  rowTitle: {
    ...typography.title2,
    fontFamily: "Lato_700Bold",
    color: theme.colors.text4,
    flex: 1,
  },
  rowTitleRight: {
    ...typography.title2,
    color: theme.colors.text4,
    fontFamily: "Lato_700Bold",
  },
  paymentDetailsRightText: {
    ...typography.title2,
    color: theme.colors.text3,
    fontWeight: fontWeights.Bold,
    fontFamily: "Lato_700Bold",
    textAlign: "right",
    marginLeft: 10,
  },
  paymentDetailsLeftText: {
    ...typography.title2,
    color: theme.colors.text3,
    fontWeight: fontWeights.Regular,
    flex: 1,
  },
  policyText: {
    ...typography.title2,
    color: theme.colors.text3,
    marginLeft: 10,
    flex: 1,
  },
  checkboxStyle: {
    width: 20,
    height: 20,
  },
  alertText: {
    ...typography.body,
    color: theme.colors.alert,
    marginLeft: 10,
    flex: 1,
  },
});

export default PaymentFlowPaymentMethodCertifiedACHYodleeWidgetReviewPayment;
